import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

const VIEWPORT_MARGIN_PX = 8;
const ANCHOR_GAP_PX = 8;

interface IPopoverAnchor {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

@Component({
  selector: 'app-popover-wrapper',
  templateUrl: './popover-wrapper.component.html',
  styleUrls: ['./popover-wrapper.component.scss'],
  standalone: false,
})
export class PopoverWrapperComponent<T = unknown> {

  @Input() component!: Type<T>;
  @Input() componentProps: Record<string, T> = {};
  @Input() cssClass?: string | string[];
  @Input() showBackdrop = true;
  @Input() backdropDismiss = true;

  @Output() readonly willDismiss$ = new EventEmitter<T>();

  @ViewChild('popoverContent', {read: ViewContainerRef, static: true})
  popoverContentRef!: ViewContainerRef;

  @ViewChild('popoverContainer', {read: ElementRef, static: true})
  private popoverContainerRef!: ElementRef<HTMLElement>;

  private innerComponentRef!: ComponentRef<T>;
  private eventValue?: MouseEvent | PointerEvent | Event;
  private anchor: IPopoverAnchor | null = null;

  isVisible = false;

  popoverX = 0;
  popoverY = 0;

  constructor(private readonly cdr: ChangeDetectorRef) {
  }

  get event(): MouseEvent | PointerEvent | Event | undefined {
    return this.eventValue;
  }

  @Input()
  set event(value: MouseEvent | PointerEvent | Event | undefined) {
    this.eventValue = value;
    this.captureAnchor(value);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isVisible) {
      return;
    }
    this.clampToViewport();
  }

  present(): void {
    this.innerComponentRef = this.popoverContentRef.createComponent(this.component);
    Object.assign(this.innerComponentRef.instance, this.componentProps);
    this.innerComponentRef.changeDetectorRef.detectChanges();
    this.clampToViewport();
    this.isVisible = true;
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.clampToViewport();
      this.cdr.detectChanges();
    });
  }

  dismiss(data?: T): void {
    this.isVisible = false;

    setTimeout(() => {
      this.willDismiss$.emit(data);
    }, 200);
  }

  onBackdropClick(): void {
    if (this.backdropDismiss) {
      this.dismiss();
    }
  }

  /**
   * Captures the trigger bounds while the originating event is still on the call stack.
   */
  private captureAnchor(event: MouseEvent | PointerEvent | Event | undefined): void {
    if (!event) {
      this.anchor = null;
      return;
    }
    if (event.currentTarget instanceof Element) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.anchor = {
        left: rect.left,
        right: rect.right,
        top: rect.top,
        bottom: rect.bottom,
      };
      return;
    }
    if ('clientX' in event && typeof event.clientX === 'number') {
      this.anchor = {
        left: event.clientX,
        right: event.clientX,
        top: event.clientY,
        bottom: event.clientY,
      };
      return;
    }
    this.anchor = null;
  }

  /**
   * Flips and clamps the popover so its box stays fully inside the viewport.
   */
  private clampToViewport(): void {
    const container = this.popoverContainerRef?.nativeElement;
    if (!container) {
      return;
    }

    const width = container.offsetWidth;
    const height = container.offsetHeight;
    if (width <= 0 || height <= 0) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const anchor = this.anchor;

    let left: number;
    let top: number;

    if (anchor) {
      left = anchor.left;
      top = anchor.bottom + ANCHOR_GAP_PX;
      if (left + width > viewportWidth - VIEWPORT_MARGIN_PX) {
        left = anchor.right - width;
      }
      if (top + height > viewportHeight - VIEWPORT_MARGIN_PX) {
        top = anchor.top - ANCHOR_GAP_PX - height;
      }
    } else {
      left = (viewportWidth - width) / 2;
      top = (viewportHeight - height) / 2;
    }

    this.popoverX = Math.min(
      Math.max(VIEWPORT_MARGIN_PX, left),
      Math.max(VIEWPORT_MARGIN_PX, viewportWidth - VIEWPORT_MARGIN_PX - width),
    );
    this.popoverY = Math.min(
      Math.max(VIEWPORT_MARGIN_PX, top),
      Math.max(VIEWPORT_MARGIN_PX, viewportHeight - VIEWPORT_MARGIN_PX - height),
    );
  }
}
